import { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  takeEvery
  } from 'redux-saga/effects';

import { Set } from '~commonInfra/utils/Set';
import { createMultipleItemResultNotificationValues, MultipleItemResultNotificationValues } from '~src/commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IErrorItem,MultipleItemResult } from '~src/commonInfra/contentActions/MultipleItemResult';
import { DataAccelerationTaskMenuAction, DataAccelerationType } from '~src/dataAcceleration/actions/DataAccelerationTaskMenuAction';
import { IAccelerationTaskInfo } from '~src/dataAcceleration/models/IAccelerationTaskInfo';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { AdminAllowAcceleration, AdminDisallowAcceleration } from '../../bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Formatters, Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { AccelerationTasks } from '../resources/AccelerationTasks';
import { getErrorMessageFor } from './DataAccelerationSaga';

function processDataAccelerationAction(contents: Set<IAccelerationTaskInfo>, dataAccelerationType: DataAccelerationType): Promise<IResult> {
  if (contents.size() < 1) {
    return null;
  }

  if (dataAccelerationType === DataAccelerationType.adminAllowAcceleration) {
    return AccelerationTasks.adminAllowDataAccelerationForViews(contents.keys());
  } else {
    return AccelerationTasks.adminDisallowDataAccelerationForViews(contents.keys());
  }
}

export function showNotification(result: IResult, contents: Set<IAccelerationTaskInfo>, dataAccelerationType: DataAccelerationType) {
  const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
    new MultipleItemResult(contents, result),
    {
      describeSuccessSummary: (successCount: number) => {
        return getSuccessSummary(successCount, dataAccelerationType);
      },
      describeErrorSummary: (errorCount: number) => {
        return getErrorSummary(errorCount, dataAccelerationType);
      },
      describeErrorDetail: (errorItem: IErrorItem<IAccelerationTaskInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getSuccessSummary(successCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.adminAllowAcceleration) {
    return Messages.turnOnDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
  }

  return Messages.turnOffDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
}

export function getErrorSummary(errorCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.adminAllowAcceleration) {
    return Messages.turnOnDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
  }
  return Messages.turnOffDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
}

export function* handleDataAccelerationAction(action: DataAccelerationTaskMenuAction): SagaIterator {
  try {
      if (action !== undefined && action.tasks !== undefined && action.tasks.length >= 1) {
        let content = Set.fromIdItemArray(action.tasks);
        const contentsResult = yield call(processDataAccelerationAction, content, action.type);
        yield call(showNotification, contentsResult, content, action.type);
        const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, action.tasks);
        if (!didReload) {
          yield put(requestSpaceContents());
        }
      }else{
          yield put(requestFailed(`At least one view needs to be selected for data acceleration`));
      }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAllowDataAccelerationSaga(): SagaIterator {
  yield takeEvery(AdminAllowAcceleration, handleDataAccelerationAction);
}

export function* rootDisallowDataAccelerationSaga(): SagaIterator {
  yield takeEvery(AdminDisallowAcceleration, handleDataAccelerationAction);
}
